import { useEffect } from 'react';
import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import type { Case } from '../../domain/initial-cases';
import { StatusBadge } from '../../components/StatusBadge';
import { CaseCustomerChip } from '../../components/CaseCustomerChip';
import { CaseProgressTracker } from '../../components/CaseProgressTracker';
import { isNewToVetting } from '../../domain/case-progress';
import { markVettingOpened } from '../../api/cases';
import { useAppData, useSelectedCase } from '../../state/contexts/AppDataContext';
import { useUIState } from '../../state/contexts/UIStateContext';
import { getCurrentUser, isAssignedAnalyst } from '../../auth/useUser';
import { OverviewTab } from './tabs/OverviewTab';
import { OnboardingTab } from './tabs/OnboardingTab';
import { KycTab } from './tabs/KycTab';
import { RcdShaftTab } from './tabs/RcdShaftTab';
import { IpTab } from './tabs/IpTab';
import { TrustTab } from './tabs/TrustTab';
import { TnsTab } from './tabs/TnsTab';
import { DecisionTab } from './tabs/DecisionTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { ChangesTab } from './tabs/ChangesTab';
import { AuditTab } from './tabs/AuditTab';

const ALL_TABS = ['overview', 'onboarding', 'kyc', 'rcd-shaft', 'ip', 'trust', 'tns', 'decision', 'notifications', 'changes', 'audit'];
const OA_TABS = ['overview', 'onboarding', 'changes', 'audit'];
const VA_TABS = ['overview', 'kyc', 'rcd-shaft', 'ip', 'trust', 'tns', 'decision', 'notifications', 'audit'];

// #695 — tabs that no longer exist, and where their content went. 'financials'
// was a tab of its own until the financial references moved inside the KYC
// checklist's Financial Stability item, which is the item they are evidence
// for. Anything still naming the old tab — a bookmarked link, a stale
// `caseTab` held in UI state from before the deploy, a link in a runbook —
// lands on the tab that now holds the content instead of on an empty panel
// under a tab strip with nothing selected.
//
// Resolved during render rather than corrected by an effect: an effect renders
// the wrong thing once and then swaps, and there is nothing to correct anyway
// — `caseTab` is component state, so the next click overwrites it.
const RETIRED_TABS: Record<string, string> = { financials: 'kyc' };

export function CaseDetail() {
  const { setPage, caseTab: requestedTab, setCaseTab, navContext, setNavContext, setModal, setModalData } = useUIState();
  const user = getCurrentUser();
  const selected = useSelectedCase();
  const { mergeLocalCase } = useAppData();
  // #766 — THE CASE'S OA/VA AGENT HAS OPENED IT, which clears its "New" badge
  // on the Cases list for everybody. Only the agent: anyone else looking in
  // would take the marker away from the person it is for, and the API refuses
  // them anyway. `analystId` is a users.id, so the comparison is against
  // `serverId` (isAssignedAnalyst — CLAUDE.md B-2). The route bumps no version,
  // so folding the stamp in locally (mergeLocalCase) cannot desynchronise the
  // If-Match chain; a failure only leaves the badge up, so it is not toasted.
  const markOpened = !!selected && isNewToVetting(selected) && isAssignedAnalyst(selected.analystId, user);
  const selectedId = selected?.id;
  useEffect(() => {
    if (!markOpened || !selectedId) return;
    markVettingOpened(selectedId)
      .then(r => mergeLocalCase(selectedId, { vettingOpenedAt: r.vettingOpenedAt }))
      .catch(() => { /* the badge stays; the next open retries */ });
  }, [markOpened, selectedId, mergeLocalCase]);
  if (!selected) return <div style={{ color: C.textMuted }}>Select a case</div>;
  const c = selected as Case;

  const caseTab = RETIRED_TABS[requestedTab] ?? requestedTab;
  const caseTabs = navContext === 'onboarding' ? OA_TABS : navContext === 'vetting' ? VA_TABS : ALL_TABS;
  // Roving tabindex base; falls back to the first tab when caseTab is not in
  // the current context's tab set (e.g. context switched while on a VA tab).
  const activeIdx = Math.max(0, caseTabs.indexOf(caseTab));

  const onTablistKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (activeIdx + 1) % caseTabs.length;
    else if (e.key === 'ArrowLeft') next = (activeIdx - 1 + caseTabs.length) % caseTabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = caseTabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    setCaseTab(caseTabs[next]);
    document.getElementById(`case-tab-${caseTabs[next]}`)?.focus();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => setPage('cases')}>← Back</button>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{c.id}</h2>
        <StatusBadge status={c.status} />
        {c.eid && <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.success, background: C.successLight, padding: '3px 8px', borderRadius: 4, fontWeight: 600 }}>EID: {c.eid}</span>}
        {/* #516 — which company this registration belongs to. `registrant` is
            the declared legal name on the application, not the tenant that owns
            it: one company can hold several registrations and two companies can
            file names that look alike. */}
        <CaseCustomerChip c={c} />
        {/* #691 — deleting the registration. Admin only, and the DELETE route
            answers 403 to anyone else, so this is the label on a locked door
            rather than the lock. Pushed to the far end of the header because it
            is the one destructive control on this screen and it should not sit
            next to the navigation; the confirmation is the type-the-name dialog
            (components/modals/DeleteRegistrationDialog.tsx), never this click.
            `onDeleted` leaves the detail view: the case it renders is gone from
            the store by then, and CaseDetail with no selected case is an empty
            panel. */}
        {user?.canAdmin && (
          <button
            type="button"
            style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall, marginLeft: 'auto' }}
            onClick={() => {
              setModalData({ case: c, onDeleted: () => setPage('cases') });
              setModal('delete-registration');
            }}
          >Delete registration</button>
        )}
      </div>

      {/* #766 — the stage banner, stepper and open items, above the tab row so
          they are on every tab in every context. An open item opens the tab
          that resolves it; when that tab is not in this context's set (a KYC
          item while working from the Onboarding queue), the full set is shown
          first, so the tab strip is not left with nothing selected. */}
      <CaseProgressTracker
        c={c}
        onOpenTab={t => {
          if (!caseTabs.includes(t)) setNavContext(null);
          setCaseTab(t);
        }}
      />

      <div style={styles.tabs} role="tablist" aria-label="Case sections" onKeyDown={onTablistKeyDown}>
        {caseTabs.map((t, i) => (
          <button key={t} type="button" role="tab" id={`case-tab-${t}`} aria-selected={caseTab === t} aria-controls={`case-tabpanel-${t}`} tabIndex={i === activeIdx ? 0 : -1} style={{ ...styles.btnUnstyled, ...styles.tab, ...(caseTab === t ? styles.tabActive : {}) }} onClick={() => setCaseTab(t)}>
            {t.replace('-', '/').replace(/^\w/, c2 => c2.toUpperCase())}
          </button>
        ))}
        {navContext && <button type="button" style={{ ...styles.btnUnstyled, ...styles.tab, marginLeft: 'auto', fontSize: 11, color: C.textDim }} onClick={() => setNavContext(null)}>Show All Tabs ›</button>}
      </div>

      <div role="tabpanel" id={`case-tabpanel-${caseTab}`} aria-labelledby={`case-tab-${caseTab}`}>
        {caseTab === 'overview' && <OverviewTab c={c} />}
        {caseTab === 'onboarding' && <OnboardingTab c={c} />}
        {caseTab === 'kyc' && <KycTab c={c} />}
        {caseTab === 'rcd-shaft' && <RcdShaftTab c={c} />}
        {caseTab === 'ip' && <IpTab c={c} />}
        {caseTab === 'trust' && <TrustTab c={c} />}
        {caseTab === 'tns' && <TnsTab c={c} />}
        {caseTab === 'decision' && <DecisionTab c={c} />}
        {caseTab === 'notifications' && <NotificationsTab c={c} />}
        {caseTab === 'changes' && <ChangesTab c={c} />}
        {caseTab === 'audit' && <AuditTab c={c} />}
      </div>
    </div>
  );
}
